import { all, call, delay, put, takeLatest } from "redux-saga/effects";
import type { SagaIterator } from "redux-saga";

import {
  marketplaceHomeFailed,
  marketplaceHomeSucceeded,
  marketplaceBrandProductsFailed,
  marketplaceBrandProductsSucceeded,
  marketplaceProductsPageFailed,
  marketplaceProductsPageSucceeded,
  marketplaceSearchCleared,
  marketplaceSearchFailed,
  marketplaceSearchSucceeded
} from "../actions/marketplace.actions";
import type {
  MarketplaceBrandProductsRequestedAction,
  MarketplaceProductsPageRequestedAction,
  MarketplaceSearchRequestedAction
} from "../actions/marketplace.actions";
import * as constants from "../constants/marketplace.constants";
import { toApiErrorState } from "../../services/api";
import {
  getBrandProducts,
  getMarketplaceHome,
  getProducts,
  searchProducts
} from "../../services/marketplace.services";
import type {
  ApiCollectionResponse,
  ApiPaginatedResponse,
  BrandProductsPayload,
  MarketplaceHomePayload,
  Product
} from "../../types/marketplace";

const searchCache = new Map<string, Product[]>();

function* marketplaceHomeGet(): SagaIterator {
  try {
    const data = (yield call(getMarketplaceHome)) as MarketplaceHomePayload;
    yield put(marketplaceHomeSucceeded(data));
  } catch (error: unknown) {
    yield put(marketplaceHomeFailed(toApiErrorState(error)));
  }
}

function* marketplaceSearch(action: MarketplaceSearchRequestedAction): SagaIterator {
  try {
    const value = action.value.trim();

    if (value.length < 3) {
      yield put(marketplaceSearchCleared());
      return;
    }

    yield delay(260);

    const cacheKey = value.toLowerCase();
    const cachedItems = searchCache.get(cacheKey);

    if (cachedItems) {
      yield put(marketplaceSearchSucceeded(cachedItems));
      return;
    }

    const data = (yield call(searchProducts, value)) as ApiCollectionResponse<Product>;
    searchCache.set(cacheKey, data.items);
    yield put(marketplaceSearchSucceeded(data.items));
  } catch (error: unknown) {
    yield put(marketplaceSearchFailed(toApiErrorState(error)));
  }
}

function* marketplaceProductsPageGet(
  action: MarketplaceProductsPageRequestedAction
): SagaIterator {
  try {
    const data = (yield call(
      getProducts,
      action.page,
      action.perPage
    )) as ApiPaginatedResponse<Product>;
    yield put(marketplaceProductsPageSucceeded(data));
  } catch (error: unknown) {
    yield put(marketplaceProductsPageFailed(toApiErrorState(error)));
  }
}

function* marketplaceBrandProductsGet(
  action: MarketplaceBrandProductsRequestedAction
): SagaIterator {
  try {
    const data = (yield call(
      getBrandProducts,
      action.clientId,
      action.page,
      action.perPage,
      action.category
    )) as BrandProductsPayload;
    yield put(marketplaceBrandProductsSucceeded(data));
  } catch (error: unknown) {
    yield put(marketplaceBrandProductsFailed(toApiErrorState(error)));
  }
}

export function* watchMarketplace(): SagaIterator {
  yield all([
    takeLatest(constants.MARKETPLACE_HOME_REQUESTED, marketplaceHomeGet),
    takeLatest(constants.MARKETPLACE_SEARCH_REQUESTED, marketplaceSearch),
    takeLatest(constants.MARKETPLACE_PRODUCTS_PAGE_REQUESTED, marketplaceProductsPageGet),
    takeLatest(constants.MARKETPLACE_BRAND_PRODUCTS_REQUESTED, marketplaceBrandProductsGet)
  ]);
}
